import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { businessService } from '../../services/businessService';
import ImageUpload from '../common/ImageUpload';
import ImageZoomPreviewModal from '../common/ImageZoomPreviewModal';
import BusinessProductsServicesSection from './BusinessProductsServicesSection';
import { societyService, extractSocietyResults } from '../../services/societyService';
import {
  saveFormData,
  loadFormData,
  clearFormData,
  pickPersistedFormFields,
  hasConsumedFormRestore,
  markFormRestoreConsumed,
  clearFormRestoreConsumed,
} from '../../utils/formPersistence';
import LoginPromptModal from '../common/LoginPromptModal';
import SocietySearchableSelect from '../common/SocietySearchableSelect';
import { getSlugFromParams } from '../../utils/urlHelpers';
import { useCurrentLocation } from '../../hooks/useCurrentLocation';

const BUSINESS_CREATE_FORM_ID = 'business-create-form';

const BusinessCreateForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Check if we're in edit mode
  // The route is /businesses/:businessSlug/edit, so businessSlug is in params
  const businessIdentifier = params.businessSlug || null;
  const isEditMode = !!businessIdentifier;
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    address: '',
    contact_phone: '',
    latitude: '',
    longitude: '',
    society: '',
    is_public: false
  });
  const [userSociety, setUserSociety] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // Existing images from the business (for edit mode)
  const [imagesToDelete, setImagesToDelete] = useState([]); // IDs of images to delete
  const [compressedImages, setCompressedImages] = useState([]);
  const [showCompressionPreview, setShowCompressionPreview] = useState(false);
  const [compressionMessage, setCompressionMessage] = useState('');
  const [hasLargeImages, setHasLargeImages] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [galleryImagePreview, setGalleryImagePreview] = useState(null); // { url, name, revokeOnClose }
  const galleryImagePreviewRef = useRef(null);
  const [displayImageIndex, setDisplayImageIndex] = useState(null); // Index of the display/face image
  const [loadedBusinessId, setLoadedBusinessId] = useState(null); // numeric id once business is loaded (edit mode)
  // Catalogue drafts captured in create mode; persisted after business is created.
  const catalogueDraftRef = useRef({ kind: null, items: [] });
  const formDraftRestoredRef = useRef(false);
  const societiesLoadedForUserRef = useRef(null);
  const businessLoadedKeyRef = useRef(null);
  const userId = user?.id;
  const societyIdFromUrl = searchParams.get('society');

  const handleCatalogueDraftChange = useCallback((next) => {
    catalogueDraftRef.current = next || { kind: null, items: [] };
  }, []);

  const setGalleryImagePreviewSafe = useCallback((next) => {
    setGalleryImagePreview((prev) => {
      if (prev?.revokeOnClose && prev.url) URL.revokeObjectURL(prev.url);
      return next;
    });
  }, []);

  useEffect(() => {
    galleryImagePreviewRef.current = galleryImagePreview;
  }, [galleryImagePreview]);

  useEffect(() => {
    return () => {
      const p = galleryImagePreviewRef.current;
      if (p?.revokeOnClose && p.url) URL.revokeObjectURL(p.url);
    };
  }, []);
  const { locLoading, captureCurrentLocation } = useCurrentLocation({
    onSuccess: ({ latitude, longitude }) => {
      setFormData((prev) => ({ ...prev, latitude, longitude }));
      showSuccess('Location captured from GPS.');
    },
    onError: (message) => {
      showError(message);
    },
  });

  const categoryOptions = [
    { value: 'shop', label: 'Shop' },
    { value: 'company', label: 'Company' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'cafe', label: 'Cafe' },
    { value: 'salon', label: 'Salon' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'school', label: 'School' },
    { value: 'gym', label: 'Gym' },
    { value: 'pharmacy', label: 'Pharmacy' },
    { value: 'bank', label: 'Bank' },
    { value: 'property_dealer', label: 'Property Dealer' },
    { value: 'other', label: 'Other' }
  ];

  // Load business data if in edit mode (once per business + user)
  useEffect(() => {
    const loadBusiness = async () => {
      if (isEditMode && businessIdentifier && userId) {
        const loadKey = `${businessIdentifier}:${userId}`;
        if (businessLoadedKeyRef.current === loadKey) {
          return;
        }
        businessLoadedKeyRef.current = loadKey;

        try {
          setLoading(true);
          setLoadingSocieties(true);
          const businessData = await businessService.getBusiness(businessIdentifier);
          setLoadedBusinessId(businessData?.id ?? null);
          
          // Load societies first so we can set the society field correctly
          const userIsAdmin = user && (
            user.is_staff || 
            user.is_superuser || 
            ['admin', 'moderator'].includes(user.role)
          );
          
          let societiesList = [];
          if (userIsAdmin) {
            const response = await societyService.getSocieties();
            societiesList = response.results || [];
          } else if (user && user.society) {
            societiesList = [user.society];
          }
          setSocieties(societiesList);
          
          // Populate form with existing business data
          setFormData({
            name: businessData.name || '',
            description: businessData.description || '',
            category: businessData.category || 'other',
            address: businessData.address || '',
            contact_phone: businessData.contact_phone || '',
            latitude:
              businessData.latitude !== null && businessData.latitude !== undefined
                ? String(businessData.latitude)
                : '',
            longitude:
              businessData.longitude !== null && businessData.longitude !== undefined
                ? String(businessData.longitude)
                : '',
            society: businessData.society?.id || businessData.society || '',
            is_public: businessData.is_public || false
          });
          
          // Set existing images if any
          if (businessData.images && businessData.images.length > 0) {
            setExistingImages(businessData.images);
            
            // Find which existing image is the display image
            if (businessData.display_image) {
              const displayImageIndex = businessData.images.findIndex(
                img => img.id === businessData.display_image.id
              );
              if (displayImageIndex >= 0) {
                // For existing images, we use negative indices to distinguish from new images
                // New images will have positive indices (0, 1, 2...)
                // Existing images will have negative indices (-1, -2, -3...)
                setDisplayImageIndex(-(displayImageIndex + 1));
              }
            }
          }
        } catch (error) {
          console.error('Error loading business:', error);
          showError('Failed to load business data');
          navigate('/businesses');
        } finally {
          setLoading(false);
          setLoadingSocieties(false);
        }
      }
    };
    
    if (isEditMode && businessIdentifier && userId) {
      loadBusiness();
    }
  }, [isEditMode, businessIdentifier, userId, navigate, showError]);

  // Restore saved business draft once per session (create mode only)
  useEffect(() => {
    if (isEditMode || formDraftRestoredRef.current || hasConsumedFormRestore('business')) {
      formDraftRestoredRef.current = true;
      return;
    }
    formDraftRestoredRef.current = true;
    markFormRestoreConsumed('business');

    const savedFields = pickPersistedFormFields(loadFormData('business'));
    if (savedFields) {
      setFormData((prev) => ({
        ...prev,
        ...savedFields,
      }));
      setSelectedImages([]);
      showSuccess('Your previous form data has been restored!');
    }
  }, [isEditMode, showSuccess]);

  // Load societies once per user (create mode); edit mode loads societies in loadBusiness
  useEffect(() => {
    if (isEditMode || !userId) {
      return;
    }

    let cancelled = false;

    const applySocietyFromUrl = (allSocieties) => {
      if (!societyIdFromUrl) {
        return;
      }
      const sid = parseInt(societyIdFromUrl, 10);
      const selectedSociety = allSocieties.find((s) => s.id === sid);
      if (selectedSociety) {
        setFormData((prev) => ({
          ...prev,
          society: prev.society || selectedSociety.id,
        }));
      }
    };

    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);

        const userIsAdmin =
          user.is_staff ||
          user.is_superuser ||
          ['admin', 'moderator'].includes(user.role);
        if (!cancelled) {
          setIsAdmin(userIsAdmin);
        }

        if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          if (cancelled) {
            return;
          }
          setSocieties(allSocieties);
          applySocietyFromUrl(allSocieties);
        } else if (user.society) {
          if (cancelled) {
            return;
          }
          setUserSociety(user.society);
          setSocieties([user.society]);
          setFormData((prev) => ({
            ...prev,
            society: prev.society || user.society.id,
          }));
        } else {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          if (cancelled) {
            return;
          }
          setSocieties(allSocieties);
          applySocietyFromUrl(allSocieties);
        }

        societiesLoadedForUserRef.current = userId;
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading societies:', error);
          showError('Failed to load societies. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSocieties(false);
        }
      }
    };

    if (societiesLoadedForUserRef.current === userId) {
      return undefined;
    }

    loadSocieties();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, userId, societyIdFromUrl, showError]);

  // Save form data whenever it changes
  useEffect(() => {
    if (formData.name || formData.description) {
      saveFormData('business', formData);
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Function to compress image to 1MB or less
  const compressImage = (file, maxSizeMB = 1) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions to reduce file size
        let { width, height } = img;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        
        // Start with 0.8 quality and reduce if needed
        let quality = 0.8;
        
        const compress = () => {
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob.size <= maxSizeBytes || quality <= 0.1) {
              // Create a new file with the compressed blob
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve({
                file: compressedFile,
                originalSize: file.size,
                compressedSize: blob.size,
                compressionRatio: ((file.size - blob.size) / file.size * 100).toFixed(1)
              });
            } else {
              // Reduce quality and try again
              quality -= 0.1;
              compress();
            }
          }, file.type, quality);
        };
        
        compress();
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Check if any images are larger than 1MB
  const checkImageSizes = (images) => {
    const largeImages = images.filter(img => img.size > 1024 * 1024);
    setHasLargeImages(largeImages.length > 0);
    return largeImages.length > 0;
  };

  // Function to preview compressed images
  const previewCompressedImages = async () => {
    if (selectedImages.length === 0) return;
    
    setLoading(true);
    try {
      const compressionResults = await Promise.all(
        selectedImages.map(async (image) => {
          if (image.size > 1024 * 1024) {
            return await compressImage(image, 1);
          }
          return {
            file: image,
            originalSize: image.size,
            compressedSize: image.size,
            compressionRatio: 0
          };
        })
      );
      
      setCompressedImages(compressionResults);
      setShowCompressionPreview(true);
      
      // Count how many images were compressed
      const compressedCount = compressionResults.filter(r => r.compressionRatio > 0).length;
      if (compressedCount > 0) {
        setCompressionMessage(`${compressedCount} image(s) will be compressed to fit the 1MB limit.`);
      } else {
        setCompressionMessage('All images are within the 1MB limit.');
      }
    } catch (error) {
      console.error('Error compressing images:', error);
      showError('Error compressing images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Persist draft products/services collected on the create form against a newly
  // created business. Each draft failure is logged but does not roll back the
  // business; the user is informed via a single toast summary so the main flow
  // (business creation) is not blocked by a single catalogue error.
  const persistCatalogueDrafts = async (createdBusiness) => {
    const { kind, items } = catalogueDraftRef.current || {};
    if (!kind || !Array.isArray(items) || items.length === 0) return;
    const targetId = createdBusiness?.id;
    if (!targetId) return;

    let succeeded = 0;
    let failed = 0;
    for (const draft of items) {
      const payload = { ...draft, images: draft._draftImages || [] };
      delete payload._draftImages;
      delete payload._tempId;
      try {
        if (kind === 'products') {
          await businessService.createProduct(targetId, payload);
        } else {
          await businessService.createService(targetId, payload);
        }
        succeeded += 1;
      } catch (err) {
        console.error('Failed to create draft catalogue item', err);
        failed += 1;
      }
    }

    if (succeeded > 0 && failed === 0) {
      showSuccess(
        `${succeeded} ${kind === 'products' ? 'product' : 'service'}${succeeded === 1 ? '' : 's'} added.`,
      );
    } else if (succeeded > 0 && failed > 0) {
      showError(
        `${succeeded} added, ${failed} failed. You can edit the business later to retry the failed ones.`,
      );
    } else if (failed > 0) {
      showError(
        `Could not add ${failed} ${kind === 'products' ? 'product' : 'service'}${failed === 1 ? '' : 's'}. You can add them by editing the business.`,
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    setLoading(true);

    try {
      // Use compressed images if preview was shown, otherwise compress now
      // IMPORTANT: We maintain the same order, so display_image_index remains valid
      let processedImages = selectedImages;
      
      if (showCompressionPreview && compressedImages.length > 0) {
        // Use pre-compressed images - order is preserved (index 0 -> index 0, etc.)
        processedImages = compressedImages.map(result => result.file);
      } else if (selectedImages.length > 0 && hasLargeImages) {
        showSuccess('Compressing images...');
        // Compress images while preserving order
        processedImages = await Promise.all(
          selectedImages.map(async (image) => {
            if (image.size > 1024 * 1024) {
              const result = await compressImage(image, 1);
              return result.file;
            }
            return image;
          })
        );
      }

      // Determine display image index
      let finalDisplayImageIndex = displayImageIndex;
      const remainingExistingImages = isEditMode ? existingImages.filter(img => !imagesToDelete.includes(img.id)) : [];
      
      if (isEditMode && businessIdentifier) {
        // For edit mode, handle existing and new images
        // If display_image_index is negative, it refers to an existing image
        if (finalDisplayImageIndex !== null && finalDisplayImageIndex < 0) {
          // Negative index: existing image selected
          const existingImageIndex = Math.abs(finalDisplayImageIndex) - 1;
          const existingImage = existingImages[existingImageIndex];
          if (existingImage && !imagesToDelete.includes(existingImage.id)) {
            // Find the index in remaining existing images (ordered by id)
            const sortedRemaining = [...remainingExistingImages].sort((a, b) => a.id - b.id);
            const indexInRemaining = sortedRemaining.findIndex(img => img.id === existingImage.id);
            if (indexInRemaining >= 0) {
              // If no new images, send index relative to existing images
              // If new images exist, we can't mix - backend limitation
              // For now, if new images are added, we'll set display to first new image
              if (processedImages.length === 0) {
                finalDisplayImageIndex = indexInRemaining;
              } else {
                // Can't use existing image as display when adding new images with current backend
                // Default to first new image
                finalDisplayImageIndex = null;
              }
            }
          } else {
            finalDisplayImageIndex = null;
          }
        } else if (finalDisplayImageIndex !== null && finalDisplayImageIndex >= 0) {
          // Positive index: new image selected
          // Index is relative to new images only (backend expects this)
          if (finalDisplayImageIndex < processedImages.length) {
            // Keep as is - it's relative to new images
          } else {
            finalDisplayImageIndex = null;
          }
        }
        
        // Default if none selected
        if (finalDisplayImageIndex === null) {
          if (processedImages.length > 0) {
            finalDisplayImageIndex = 0; // First new image
          } else if (remainingExistingImages.length > 0) {
            finalDisplayImageIndex = 0; // First existing image
          }
        }
      } else {
        // For create mode, just use the index directly
        if (finalDisplayImageIndex === null && processedImages.length > 0) {
          finalDisplayImageIndex = 0;
        }
      }

      const businessData = {
        ...formData,
        images: processedImages,
      };
      
      // Include images_to_delete for edit mode
      if (isEditMode && imagesToDelete.length > 0) {
        businessData.images_to_delete = imagesToDelete;
      }
      
      // Include display_image_index if we have a valid index
      if (finalDisplayImageIndex !== null && typeof finalDisplayImageIndex === 'number' && finalDisplayImageIndex >= 0) {
        if (isEditMode && processedImages.length === 0) {
          // No new images: index is relative to existing images (after deletion)
          // Backend will use this index on the remaining existing images
          businessData.display_image_index = finalDisplayImageIndex;
        } else if (processedImages.length > 0) {
          // New images: index is relative to new images only
          if (finalDisplayImageIndex < processedImages.length) {
            businessData.display_image_index = finalDisplayImageIndex;
          }
        }
      } else if (isEditMode && processedImages.length === 0 && remainingExistingImages.length > 0 && finalDisplayImageIndex === null) {
        // If no display image selected but we have existing images, default to first
        businessData.display_image_index = 0;
      }
      if (isEditMode && businessIdentifier) {
        // Update existing business
        const updatedBusiness = await businessService.updateBusiness(businessIdentifier, businessData);
        showSuccess('Business updated successfully!');
        // Navigate back to business detail page
        navigate(`/businesses/${updatedBusiness.slug || updatedBusiness.id}`);
      } else {
        // Create new business
        const createdBusiness = await businessService.createBusiness(businessData);
        // Persist any catalogue drafts (products OR services) collected on the form.
        await persistCatalogueDrafts(createdBusiness);
        // Clear saved form data on successful submission
        clearFormData('business');
        clearFormRestoreConsumed('business');
        showSuccess('Business created successfully! It will be reviewed by admin before being published.');
        
        // Check if we're in a society context and navigate back appropriately
        const isSocietyContext = location.pathname.includes('/societies/');
        if (isSocietyContext) {
          // Extract society slug from the current path
          const pathParts = location.pathname.split('/');
          const societySlugIndex = pathParts.indexOf('societies') + 1;
          const societySlug = pathParts[societySlugIndex];
          navigate(`/societies/${societySlug}/businesses`);
        } else {
          navigate('/businesses');
        }
      }
    } catch (error) {
      console.error('Error creating business:', error);
      showError('Failed to create business. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    navigate('/?auth=login');
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  if (isEditMode && loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading business data...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Login Required</h2>
        <p className="text-gray-600 mb-6">You need to be logged in to create a business listing.</p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
        >
          Login
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{isEditMode ? 'Edit Business' : 'Create New Business'}</h1>
      
      {/* Approval Notice - Only show for new businesses */}
      {!isEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Business Approval Required
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>Your business will be reviewed by an administrator before being published. You'll be notified once it's approved.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <form id={BUSINESS_CREATE_FORM_ID} onSubmit={handleSubmit} className="space-y-6">
        {/* Business Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Business Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter business name"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Describe your business..."
          />
        </div>

        {/* Category */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
            Address
          </label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter business address"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="block text-sm font-medium text-gray-900">Precise location (optional)</span>
              <span className="block text-xs text-gray-500">
                Use your current GPS location on mobile/portable devices.
              </span>
            </div>
            <button
              type="button"
              onClick={captureCurrentLocation}
              disabled={locLoading}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {locLoading ? 'Getting location…' : 'Use Current Location'}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                readOnly
                tabIndex={-1}
                autoComplete="off"
                aria-readonly="true"
                placeholder="Use “Use Current Location”"
                className="w-full cursor-default px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                readOnly
                tabIndex={-1}
                autoComplete="off"
                aria-readonly="true"
                placeholder="Use “Use Current Location”"
                className="w-full cursor-default px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Contact Phone */}
        <div>
          <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
            Contact Phone
          </label>
          <input
            type="tel"
            id="contact_phone"
            name="contact_phone"
            value={formData.contact_phone}
            onChange={(e) => {
              // Only allow digits, spaces, and commas
              const value = e.target.value.replace(/[^\d\s,]/g, '');
              setFormData(prev => ({ ...prev, contact_phone: value }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., 03001234567, 04231234567"
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter digits only. You can add multiple numbers separated by space or comma (e.g., 03001234567, 04231234567)
          </p>
        </div>

        {/* Society Selection */}
        <div>
          <SocietySearchableSelect
            societies={societies}
            value={formData.society}
            onChange={handleChange}
            showCity={false}
            loading={loadingSocieties}
            disabled={loadingSocieties}
          />
          {!isAdmin && userSociety && (
            <p className="text-sm text-gray-500 mt-1">
              You can only create businesses for your own society.
            </p>
          )}
          {!isAdmin && !userSociety && societies.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Select the society for this listing. Submissions are reviewed before publication.
            </p>
          )}
        </div>

        {/* Images */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Images
          </label>
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={(images) => {
              setSelectedImages(images);
              checkImageSizes(images);
              // Reset display image if it's out of bounds
              if (displayImageIndex !== null && displayImageIndex >= images.length) {
                setDisplayImageIndex(null);
              }
            }}
            maxImages={5}
          />
          
          {/* Image Gallery with Display Image Selection */}
          {(existingImages.length > 0 || selectedImages.length > 0) && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Image Gallery</h4>
                <span className="text-xs text-gray-500">
                  Click an image to set it as the display/face image (shown on cards and detail page). Use
                  Zoom preview on each tile to inspect with adjustable zoom.
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Existing Images (for edit mode) */}
                {existingImages.map((image, index) => {
                  const existingIndex = -(index + 1); // Negative indices for existing images
                  const isDisplayImage = displayImageIndex === existingIndex;
                  const isMarkedForDeletion = imagesToDelete.includes(image.id);
                  
                  return (
                    <div
                      key={`existing-${image.id}`}
                      className={`relative group border-2 rounded-lg overflow-hidden transition-all ${
                        isDisplayImage
                          ? 'border-blue-500 ring-2 ring-blue-300'
                          : isMarkedForDeletion
                          ? 'border-red-500 opacity-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isMarkedForDeletion) {
                            setDisplayImageIndex(existingIndex);
                          }
                        }}
                      >
                        <img
                          src={image.original_image}
                          alt={`Existing image ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                      </div>
                      
                      {/* Display Image Badge */}
                      {isDisplayImage && (
                        <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Display
                        </div>
                      )}
                      
                      {/* Delete Button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isMarkedForDeletion) {
                            const newDeleteList = imagesToDelete.filter(id => id !== image.id);
                            setImagesToDelete(newDeleteList);
                            // If this was the display image and we're restoring it, we can set it back
                            // But we need to recalculate the index since other images might have been deleted
                            const remainingAfterRestore = existingImages.filter(img => !newDeleteList.includes(img.id));
                            const restoredIndex = remainingAfterRestore.findIndex(img => img.id === image.id);
                            if (restoredIndex >= 0 && displayImageIndex === null) {
                              // If no display image is set, set this restored one
                              setDisplayImageIndex(-(restoredIndex + 1));
                            }
                          } else {
                            setImagesToDelete([...imagesToDelete, image.id]);
                            // If this was the display image, clear it
                            if (displayImageIndex === existingIndex) {
                              setDisplayImageIndex(null);
                            }
                          }
                        }}
                        className={`absolute top-2 right-2 text-xs px-2 py-1 rounded z-10 ${
                          isMarkedForDeletion
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                        title={isMarkedForDeletion ? 'Restore image' : 'Delete image'}
                      >
                        {isMarkedForDeletion ? 'Restore' : 'Delete'}
                      </button>
                      
                      {/* Click to set as display image hint */}
                      {!isDisplayImage && !isMarkedForDeletion && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center pointer-events-none">
                          <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Click to set as display
                          </span>
                        </div>
                      )}
                      
                      {/* Image info overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-2 z-[5]">
                        <p className="text-white text-xs truncate">
                          Existing Image {index + 1}
                        </p>
                        {!isMarkedForDeletion && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setGalleryImagePreviewSafe({
                                url: image.original_image,
                                name: `Existing image ${index + 1}`,
                                revokeOnClose: false,
                              });
                            }}
                            className="mt-1 text-left text-xs text-white/95 underline hover:text-white"
                          >
                            Zoom preview
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* New Images */}
                {selectedImages.map((image, index) => {
                  // Get the correct image URL (use compressed if available, otherwise original)
                  let imageUrl;
                  let imageSize;
                  if (showCompressionPreview && compressedImages[index]) {
                    imageUrl = URL.createObjectURL(compressedImages[index].file);
                    imageSize = compressedImages[index].compressedSize;
                  } else {
                    imageUrl = URL.createObjectURL(image);
                    imageSize = image.size;
                  }
                  // For new images, index is offset by existing images count
                  const newImageIndex = existingImages.filter(img => !imagesToDelete.includes(img.id)).length + index;
                  const isDisplayImage = displayImageIndex === newImageIndex;
                  
                  return (
                    <div
                      key={index}
                      className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${
                        isDisplayImage
                          ? 'border-blue-500 ring-2 ring-blue-300'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setDisplayImageIndex(newImageIndex)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Business image ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      
                      {/* Display Image Badge */}
                      {isDisplayImage && (
                        <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Display Image
                        </div>
                      )}
                      
                      {/* Click to set as display image hint */}
                      {!isDisplayImage && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Click to set as display image
                          </span>
                        </div>
                      )}
                      
                      {/* Image info overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-2 z-[5]">
                        <p className="text-white text-xs truncate">
                          {image.name || `Image ${index + 1}`}
                        </p>
                        <p className="text-white/80 text-xs">
                          {(imageSize / 1024 / 1024).toFixed(1)} MB
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const file =
                              showCompressionPreview && compressedImages[index]
                                ? compressedImages[index].file
                                : image;
                            const url = URL.createObjectURL(file);
                            setGalleryImagePreviewSafe({
                              url,
                              name: image.name || `Image ${index + 1}`,
                              revokeOnClose: true,
                            });
                          }}
                          className="mt-1 text-left text-xs text-white/95 underline hover:text-white"
                        >
                          Zoom preview
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {displayImageIndex === null && selectedImages.length > 0 && (
                <p className="mt-2 text-sm text-amber-600">
                  💡 Tip: Click an image above to set it as the display/face image (shown on business cards and detail page)
                </p>
              )}
            </div>
          )}
          
          {/* Image Size Warning - Only show if there are large images */}
          {selectedImages.length > 0 && hasLargeImages && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm text-yellow-800">
                    <strong>Image Size Notice:</strong> Some images are larger than 1MB and will be automatically compressed.
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Maximum allowed size: 1MB per image. Supported formats: JPG, PNG, GIF
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Preview Compressed Images Button - Only show if there are large images */}
          {selectedImages.length > 0 && hasLargeImages && !showCompressionPreview && (
            <div className="mt-3">
              <button
                type="button"
                onClick={previewCompressedImages}
                disabled={loading}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Preview Compressed Images'}
              </button>
            </div>
          )}
          
          {/* Compression Preview */}
          {showCompressionPreview && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Compressed Images Preview</h4>
                <button
                  type="button"
                  onClick={() => setShowCompressionPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{compressionMessage}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {compressedImages.map((result, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(result.file)}
                      alt={`Compressed ${index + 1}`}
                      className="w-full h-20 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setZoomedImage({
                        src: URL.createObjectURL(result.file),
                        name: result.file.name,
                        originalSize: result.originalSize,
                        compressedSize: result.compressedSize,
                        compressionRatio: result.compressionRatio
                      })}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 rounded-b">
                      <div className="flex justify-between">
                        <span>{(result.compressedSize / 1024 / 1024).toFixed(1)}MB</span>
                        {result.compressionRatio > 0 && (
                          <span className="text-green-300">-{result.compressionRatio}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Public Visibility */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_public"
            name="is_public"
            checked={formData.is_public}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
            Make this business visible to all users
          </label>
        </div>
      </form>

      {/* Outside parent form: nested submit was triggering business save + restore toast */}
      <div className="mt-6">
        <BusinessProductsServicesSection
          mode={isEditMode ? 'edit' : 'create'}
          businessId={isEditMode ? loadedBusinessId : null}
          onDraftChange={handleCatalogueDraftChange}
        />
      </div>

      <div className="flex justify-end mt-6">
        <button
          type="submit"
          form={BUSINESS_CREATE_FORM_ID}
          disabled={loading || !formData.society}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Business' : 'Create Business')}
        </button>
      </div>

      {/* Login Modal */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onLogin={handleLogin}
        title="Login Required to Create Business"
        message="You need to be logged in to create a business. Your form data has been saved and will be restored after login."
      />

      <ImageZoomPreviewModal
        isOpen={!!galleryImagePreview}
        imageUrl={galleryImagePreview?.url}
        imageName={galleryImagePreview?.name}
        onClose={() => setGalleryImagePreviewSafe(null)}
      />

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl font-bold"
            >
              ×
            </button>
            <div className="bg-white rounded-lg p-4 max-h-[90vh] overflow-auto">
              <img
                src={zoomedImage.src}
                alt={zoomedImage.name}
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />
              <div className="mt-4 text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{zoomedImage.name}</h3>
                <div className="flex justify-center space-x-4 text-sm text-gray-600">
                  <span>Original: {(zoomedImage.originalSize / 1024 / 1024).toFixed(1)}MB</span>
                  <span>Compressed: {(zoomedImage.compressedSize / 1024 / 1024).toFixed(1)}MB</span>
                  {zoomedImage.compressionRatio > 0 && (
                    <span className="text-green-600">Saved: {zoomedImage.compressionRatio}%</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCreateForm;
